/**
 * Geo grouping used by the Countries filter.
 *
 * `LATAM T2`, `LATAM T3` and `Africa` are fixed business definitions and must not be changed
 * without a product decision. Every other group is purely geographic, so that a country is always
 * found where geography says it is and no group cuts across regions. Value tiering is deliberately
 * kept out of them: it is a business judgment that only the fixed groups above encode.
 *
 * A country belongs to exactly one group, and countries that match none fall back to `Other`.
 */

export interface CountryGroup {
  id: string;
  label: string;
  countries: string[];
  /**
   * Residual buckets are listed right after the group they complete rather than alphabetically, so
   * that a family reads as a whole (`LATAM T2`, `LATAM T3`, `LATAM Other`). Falls back to the
   * alphabetical position when the group it points at has no countries to show.
   */
  sortAfter?: string;
}

export const OTHER_COUNTRY_GROUP_ID = 'OTHER';

/**
 * Declaration order is only for readability — the fixed groups come first here, but the filter
 * renders them alphabetically. See {@link sortGroups}.
 */
export const COUNTRY_GROUPS: CountryGroup[] = [
  {
    id: 'LATAM_T2',
    label: 'LATAM T2',
    countries: ['MX', 'CL', 'PE', 'GT', 'HN', 'DO', 'SV'],
  },
  {
    id: 'LATAM_T3',
    label: 'LATAM T3',
    countries: ['BR', 'CO', 'AR', 'EC', 'VE', 'CR', 'BO', 'PA', 'PY'],
  },
  {
    id: 'AFRICA',
    label: 'Africa',
    countries: ['ZA', 'NG', 'KE', 'MA', 'EG', 'DZ', 'GH', 'TN', 'TZ', 'MG', 'UG', 'ET', 'MZ', 'CM', 'NA', 'RW'],
  },
  {
    id: 'NORTH_AMERICA',
    label: 'North America',
    countries: ['US', 'CA', 'PM'],
  },
  {
    id: 'WESTERN_EUROPE',
    label: 'Western Europe',
    countries: [
      'GB',
      'DE',
      'FR',
      'IT',
      'ES',
      'NL',
      'BE',
      'AT',
      'CH',
      'PT',
      'IE',
      'LU',
      'MT',
      'MC',
      'LI',
      'AD',
      'SM',
      'VA',
      'GI',
      'IM',
      'JE',
      'GG',
    ],
  },
  {
    id: 'NORDICS',
    label: 'Nordics',
    countries: ['SE', 'NO', 'DK', 'FI', 'IS', 'FO', 'GL', 'AX'],
  },
  {
    id: 'CEE',
    label: 'Central & Eastern Europe',
    countries: [
      'PL',
      'CZ',
      'SK',
      'HU',
      'RO',
      'BG',
      'HR',
      'SI',
      'RS',
      'BA',
      'MK',
      'ME',
      'AL',
      'XK',
      'GR',
      'CY',
      'EE',
      'LV',
      'LT',
    ],
  },
  {
    id: 'CIS',
    label: 'CIS',
    countries: ['RU', 'UA', 'BY', 'MD', 'KZ', 'UZ', 'AZ', 'AM', 'GE', 'KG', 'TJ', 'TM'],
  },
  {
    id: 'MIDDLE_EAST',
    label: 'Middle East',
    countries: ['SA', 'AE', 'QA', 'KW', 'BH', 'OM', 'IL', 'TR', 'JO', 'LB', 'IQ', 'IR', 'YE', 'SY', 'PS'],
  },
  {
    id: 'GREATER_CHINA',
    label: 'Greater China',
    countries: ['CN', 'HK', 'TW', 'MO'],
  },
  {
    id: 'JAPAN_KOREA',
    label: 'Japan & Korea',
    countries: ['JP', 'KR', 'KP'],
  },
  {
    id: 'SEA',
    label: 'Southeast Asia',
    countries: ['ID', 'TH', 'VN', 'PH', 'MY', 'SG', 'MM', 'KH', 'LA', 'BN', 'TL'],
  },
  {
    id: 'SOUTH_ASIA',
    label: 'South Asia',
    countries: ['IN', 'PK', 'BD', 'LK', 'NP', 'BT', 'MV', 'AF'],
  },
  {
    id: 'LATAM_OTHER',
    label: 'LATAM Other',
    sortAfter: 'LATAM_T3',
    countries: [
      'UY',
      'PR',
      'NI',
      'CU',
      'BZ',
      'GY',
      'SR',
      'GF',
      'JM',
      'TT',
      'BS',
      'BB',
      'HT',
      'DM',
      'GD',
      'LC',
      'VC',
      'AG',
      'KN',
      'AW',
      'CW',
      'SX',
      'BQ',
      'TC',
      'KY',
      'VG',
      'VI',
      'BM',
      'MQ',
      'GP',
      'MF',
      'BL',
      'AI',
      'MS',
    ],
  },
  {
    id: 'REST_OF_AFRICA',
    label: 'Rest of Africa',
    sortAfter: 'AFRICA',
    countries: [
      'SN',
      'CI',
      'ZM',
      'ZW',
      'BW',
      'AO',
      'CD',
      'CG',
      'ML',
      'BF',
      'NE',
      'TD',
      'GN',
      'BJ',
      'TG',
      'BI',
      'SO',
      'SD',
      'SS',
      'LY',
      'MR',
      'GM',
      'GW',
      'SL',
      'LR',
      'CF',
      'GQ',
      'GA',
      'ST',
      'CV',
      'KM',
      'SC',
      'MU',
      'LS',
      'SZ',
      'DJ',
      'ER',
      'EH',
      'YT',
      'RE',
      'SH',
    ],
  },
  {
    id: 'OCEANIA',
    label: 'Oceania',
    countries: [
      'AU',
      'NZ',
      'FJ',
      'PG',
      'NC',
      'PF',
      'SB',
      'VU',
      'WS',
      'TO',
      'GU',
      'MP',
      'AS',
      'KI',
      'FM',
      'MH',
      'NR',
      'PW',
      'TV',
      'CK',
      'NU',
      'TK',
      'WF',
      'NF',
      'CX',
      'CC',
    ],
  },
  {
    id: OTHER_COUNTRY_GROUP_ID,
    label: 'Other',
    countries: [],
  },
];

const GROUP_ID_BY_COUNTRY = new Map<string, string>(
  COUNTRY_GROUPS.flatMap((group) => group.countries.map((country) => [country, group.id] as const))
);

const GROUP_BY_ID = new Map<string, CountryGroup>(COUNTRY_GROUPS.map((group) => [group.id, group]));

const countryCodeToNameMapper = new Intl.DisplayNames(['en'], { type: 'region' });

/** Group headers are options too, so their ids are namespaced to never collide with a country code. */
export const COUNTRY_GROUP_OPTION_ID_PREFIX = 'country-group:';

/** Resolves the geo group of an ISO 3166-1 alpha-2 country code, falling back to `Other`. */
export function getCountryGroupId(countryCode?: string): string {
  return (countryCode && GROUP_ID_BY_COUNTRY.get(countryCode.toUpperCase())) || OTHER_COUNTRY_GROUP_ID;
}

/** Turns an ISO 3166-1 alpha-2 country code into its English name, falling back to the code itself. */
function getCountryName(countryCode: string): string {
  let countryName = countryCode;
  try {
    countryName = countryCodeToNameMapper.of(countryCode) ?? countryCode;
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, no-console
    console.error('Invalid country code', { countryCode, error });
  }

  return countryName;
}

/**
 * Formats a country for display, keeping the raw code around so that it stays searchable and
 * unambiguous, e.g. `Portugal (PT)`.
 */
export function getCountryLabel(countryCode: string): string {
  const countryName = getCountryName(countryCode);
  return countryName === countryCode ? countryCode : `${countryName} (${countryCode})`;
}

export function getCountryGroupLabel(groupId: string): string {
  return GROUP_BY_ID.get(groupId)?.label ?? groupId;
}

/** Groups are listed alphabetically, except for the `Other` catch-all which is pinned last. */
const compareGroups = (a: CountryGroup, b: CountryGroup) => {
  if (a.id === OTHER_COUNTRY_GROUP_ID) {
    return 1;
  }
  if (b.id === OTHER_COUNTRY_GROUP_ID) {
    return -1;
  }
  return a.label.localeCompare(b.label);
};

/**
 * Sorts the groups alphabetically, then moves every residual bucket next to the group it completes.
 * A bucket whose {@link CountryGroup.sortAfter} group is not in the list keeps its alphabetical spot.
 */
function sortGroups(groups: CountryGroup[]): CountryGroup[] {
  const isAnchored = (group: CountryGroup) => groups.some(({ id }) => id === group.sortAfter);

  const sortedGroups = groups.filter((group) => !isAnchored(group)).sort(compareGroups);

  groups
    .filter(isAnchored)
    .forEach((group) => sortedGroups.splice(sortedGroups.findIndex(({ id }) => id === group.sortAfter) + 1, 0, group));

  return sortedGroups;
}

/**
 * Buckets the given country codes into geo groups, sorted by {@link sortGroups}, and preserving the
 * order in which the countries were received (the API sorts them by the base metric) inside each
 * group. Groups without any country are omitted.
 */
export function groupCountries(countryCodes: string[]): CountryGroup[] {
  const countriesByGroup = new Map<string, string[]>();

  countryCodes.forEach((countryCode) => {
    const groupId = getCountryGroupId(countryCode);
    const countries = countriesByGroup.get(groupId);
    if (countries) {
      countries.push(countryCode);
    } else {
      countriesByGroup.set(groupId, [countryCode]);
    }
  });

  return sortGroups(COUNTRY_GROUPS.filter((group) => countriesByGroup.has(group.id))).map((group) => ({
    ...group,
    countries: countriesByGroup.get(group.id) ?? [],
  }));
}
